import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardStat } from '@features/ui';
import {
  CustomerIcon,
  ProductsIcon,
  RevenueIcon,
  TopAffiliateIcon,
} from '@features/ui/icons';
import { CartActivity } from '@features/admin/dashboard/CartActivity.tsx';
import {
  getEcommerceDashboardChart,
  getEcommerceDashboardSummary,
} from '@src/services/reports';
import {
  formatCentsAsDollars,
  formatCentsAsDollarsShortForm,
} from '@lib/utils';
import { toast } from 'react-toastify';

import { RevenueBreakdownDialog } from './RevenueBreakdownDialog';
import { TopSellingProductsDialog } from './TopSellingProductsDialog';
import { LTVDistributionDialog } from './LTVDistributionDialog';

interface ECommerceDashboardProps {
  startDate?: string | null;
  endDate?: string | null;
}

export const ECommerceDashboard: FC<ECommerceDashboardProps> = ({
  startDate,
  endDate,
}) => {
  const [revenueDialogOpen, setRevenueDialogOpen] = useState(false);
  const [productsDialogOpen, setProductsDialogOpen] = useState(false);
  const [ltvDialogOpen, setLtvDialogOpen] = useState(false);
  const [summaryData, setSummaryData] = useState({
    total_revenue_mdt: 0,
    total_products_sold: 0,
    average_customer_ltv: 0,
    top_affiliate: 0,
  });
  const [chartData, setChartData] = useState<any[]>([]);

  const fetchSummaryData = useCallback(async () => {
    try {
      const data = await getEcommerceDashboardSummary({
        start_date: startDate || null,
        end_date: endDate || null,
      });

      setSummaryData(data);
    } catch (error) {
      console.error('Failed to fetch dashboard summary:', error);
      toast.error('Failed to load dashboard summary. Please try again later.');
    }
  }, [startDate, endDate]);

  const fetchChartData = useCallback(async () => {
    try {
      const response = await getEcommerceDashboardChart({
        start_date: startDate || null,
        end_date: endDate || null,
      });

      const transformedData = response.months.map((month, index) => {
        const abandonmentSeries = response.series.find(
          (s) => s.type === 'abandonment'
        );
        const clickThroughSeries = response.series.find(
          (s) => s.type === 'clickthrough'
        );

        return {
          date: month,
          abandonment: abandonmentSeries?.data[index] || 0,
          clickThrough: clickThroughSeries?.data[index] || 0,
        };
      });

      setChartData(transformedData);
    } catch (error) {
      console.error('Failed to fetch chart data:', error);
      toast.error('Failed to load cart activity data. Please try again later.');
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchChartData();
  }, [fetchChartData]);

  useEffect(() => {
    fetchSummaryData();
  }, [fetchSummaryData]);

  const data = useMemo(
    () => [
      {
        id: '1',
        desc: 'Total Revenue MDT',
        value: formatCentsAsDollarsShortForm(summaryData.total_revenue_mdt),
        icon: <RevenueIcon />,
        onClick: () => setRevenueDialogOpen(true),
      },
      {
        id: '2',
        desc: 'Total Products Sold',
        value: summaryData.total_products_sold.toLocaleString(),
        icon: <ProductsIcon />,
        onClick: () => setProductsDialogOpen(true),
      },
      {
        id: '3',
        desc: 'Top Affiliates',
        value: formatCentsAsDollars(summaryData.top_affiliate),
        icon: <TopAffiliateIcon />,
        onClick: undefined, // API under development
      },
      {
        id: '4',
        desc: 'Average Customer LTV',
        value: formatCentsAsDollars(summaryData.average_customer_ltv),
        icon: <CustomerIcon />,
        onClick: () => setLtvDialogOpen(true),
      },
    ],
    [summaryData]
  );

  return (
    <>
      <div className={'flex flex-col gap-y-5'}>
        <div className={'flex gap-5 flex-wrap'}>
          {data.map((item) => {
            return (
              <CardStat
                key={item.id}
                className={
                  item.onClick
                    ? 'cursor-pointer hover:opacity-80 transition-opacity'
                    : ''
                }
                desc={item.desc}
                icon={
                  <div
                    className={
                      'bg-[#00224A] h-13 w-13 flex items-center justify-center rounded-[16px]'
                    }
                  >
                    {item.icon}
                  </div>
                }
                value={item.value}
                onClick={item.onClick}
              />
            );
          })}
        </div>
        <Card className={'bg-card-stat px-0'}>
          <CartActivity data={chartData} />
        </Card>
      </div>

      <RevenueBreakdownDialog
        endDate={endDate}
        open={revenueDialogOpen}
        startDate={startDate}
        onOpenChange={setRevenueDialogOpen}
      />
      <TopSellingProductsDialog
        endDate={endDate}
        open={productsDialogOpen}
        startDate={startDate}
        onOpenChange={setProductsDialogOpen}
      />
      <LTVDistributionDialog
        endDate={endDate}
        open={ltvDialogOpen}
        startDate={startDate}
        onOpenChange={setLtvDialogOpen}
      />
    </>
  );
};
